// Auth Manager - Singleton para coordinar autenticación
// Evita múltiples llamadas simultáneas de accessToken y authenticate
import AuthService from 'modules/Auth/services/auth'
import storageNames from 'constants/storageNames'
import { startAutoRefresh, stopAutoRefresh } from './axios-instance'

class AuthManager {
	// Constructor para inicializar las propiedades
	constructor() {
		this.isAuthenticating = false
		this.authPromise = null
		this.userInfoPromise = null
		this.authenticatePromise = null
		this.isInitialized = false
		this.initializationComplete = false
	}

	// Singleton pattern
	static getInstance() {
		if (!AuthManager.instance) {
			AuthManager.instance = new AuthManager()
		}
		return AuthManager.instance
	}

	// Coordinar authenticate - evita duplicados
	async authenticateUser() {
		// Si ya se completó la inicialización, evitar llamadas adicionales
		if (this.initializationComplete) {
			return Promise.resolve({ status: 200, message: 'Already initialized' })
		}

		// Si ya está inicializado (pero no completado), esperar a que se complete
		if (this.isInitialized) {
			return new Promise(resolve => {
				const checkComplete = () => {
					if (this.initializationComplete) {
						resolve({ status: 200, message: 'Already initialized' })
					} else {
						setTimeout(checkComplete, 100)
					}
				}
				checkComplete()
			})
		}

		// Si ya hay una autenticación en proceso, esperar a que termine
		if (this.authenticatePromise) {
			return this.authenticatePromise
		}

		// Si ya está autenticando, esperar
		if (this.isAuthenticating) {
			return new Promise(resolve => {
				const checkAuth = () => {
					if (!this.isAuthenticating) {
						resolve(this.authenticatePromise)
					} else {
						setTimeout(checkAuth, 100)
					}
				}
				checkAuth()
			})
		}

		// Marcar como autenticando y inicializado
		this.isAuthenticating = true
		this.isInitialized = true

		// Crear nueva promesa de autenticación
		this.authenticatePromise = AuthService.authenticateUser()
			.then(response => {
				return response
			})
			.catch(error => {
				throw error
			})
			.finally(() => {
				this.isAuthenticating = false
				this.authenticatePromise = null
			})

		return this.authenticatePromise
	}

	// Coordinar getUserInfo - evita duplicados
	async getUserInfo(userId) {
		// Si ya se completó la inicialización, evitar llamadas adicionales
		if (this.initializationComplete) {
			return Promise.resolve({ status: 200, message: 'Already initialized' })
		}

		// Si ya está inicializado pero no hay autenticación en curso, permitir la llamada
		// Esto permite llamadas después de autenticar exitosamente
		if (this.isInitialized && !this.isAuthenticating && !this.authenticatePromise) {
			// Si ya hay una llamada en proceso para el mismo usuario, esperar
			if (this.userInfoPromise) {
				return this.userInfoPromise
			}

			// Crear nueva promesa de getUserInfo
			this.userInfoPromise = AuthService.getUserInfo({ userId })
				.then(response => {
					return response
				})
				.catch(error => {
					throw error
				})
				.finally(() => {
					this.userInfoPromise = null
				})

			return this.userInfoPromise
		}

		// Si ya está inicializado (pero no completado) y hay autenticación en curso, esperar a que se complete
		if (this.isInitialized) {
			return new Promise(resolve => {
				const checkComplete = () => {
					if (this.initializationComplete) {
						resolve({ status: 200, message: 'Already initialized' })
					} else if (!this.isAuthenticating && !this.authenticatePromise) {
						// Si ya no hay autenticación en curso, permitir la llamada
						AuthService.getUserInfo({ userId })
							.then(response => resolve(response))
							.catch(error => resolve({ status: 500, message: error.message }))
					} else {
						setTimeout(checkComplete, 100)
					}
				}
				checkComplete()
			})
		}

		// Si ya hay una llamada en proceso para el mismo usuario, esperar
		if (this.userInfoPromise) {
			return this.userInfoPromise
		}

		// Crear nueva promesa de getUserInfo
		this.userInfoPromise = AuthService.getUserInfo({ userId })
			.then(response => {
				return response
			})
			.catch(error => {
				throw error
			})
			.finally(() => {
				this.userInfoPromise = null
			})

		return this.userInfoPromise
	}

	// Marcar inicialización como completa
	markInitializationComplete() {
		this.initializationComplete = true
	}

	// Limpiar estado (útil para logout)
	clearState() {
		this.isAuthenticating = false
		this.authPromise = null
		this.userInfoPromise = null
		this.authenticatePromise = null
		this.isInitialized = false
		this.initializationComplete = false
		// Detener refresh automático de Gigya
		stopAutoRefresh()
	}

	// Verificar si ya está autenticado
	isAlreadyAuthenticated() {
		return (
			localStorage.getItem(storageNames.gigyaToken) !== null ||
			localStorage.getItem(storageNames.cognitoToken) !== null ||
			localStorage.getItem(storageNames.authToken) !== null
		)
	}

	// Iniciar refresh automático (útil después del login)
	startRefreshAuto() {
		startAutoRefresh()
	}
}

// Exportar instancia singleton
export default AuthManager.getInstance()
